import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { timeZones } from 'utils/timeZones';
import {
  REPORT_TIME_OPTIONS,
  WEEKDAYS,
  formatWeeklyReportSchedule,
  getTimeZoneLabel,
  validateWeeklyReportSchedule,
} from 'utils/orgSchedule';

/**
 * An organization's timezone and weekly report day/time, controlled by its
 * parent.
 *
 * The three go together: the cron matches an exact weekday + time in the org's
 * own zone, so changing the zone moves the report even though the clock time
 * reads the same. The time list is the 5-minute grid the API accepts -- an
 * off-grid time is a 400, not a rounding.
 */
const OrgScheduleEditor = ({ disabled, onChange, value }) => {
  const error = validateWeeklyReportSchedule(value);

  const update = (changes) => onChange({ ...value, ...changes });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <FormControl sx={{ minWidth: 280 }} size="small">
        <InputLabel id="org-timezone-label">Timezone</InputLabel>
        <Select
          labelId="org-timezone-label"
          label="Timezone"
          id="org-timezone-select"
          name="orgTimeZone"
          size="small"
          disabled={disabled}
          MenuProps={{ disableScrollLock: true }}
          value={value?.timeZone || ''}
          onChange={(e) => update({ timeZone: e.target.value })}
        >
          {timeZones?.map((tz) => (
            <MenuItem key={tz?.value} value={tz?.utc?.[0]}>
              <Typography variant="body2">{tz?.text}</Typography>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <FormControl sx={{ minWidth: 180 }} size="small">
          <InputLabel id="report-weekday-label">Day</InputLabel>
          <Select
            labelId="report-weekday-label"
            label="Day"
            id="report-weekday-select"
            name="reportWeekday"
            size="small"
            disabled={disabled}
            MenuProps={{ disableScrollLock: true }}
            value={value?.weekday || ''}
            onChange={(e) => update({ weekday: e.target.value })}
          >
            {WEEKDAYS.map((day) => (
              <MenuItem key={day.value} value={day.value}>
                <Typography variant="body2">{day.label}</Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 180 }} size="small">
          <InputLabel id="report-time-label">Time</InputLabel>
          <Select
            labelId="report-time-label"
            label="Time"
            id="report-time-select"
            name="reportTime"
            size="small"
            disabled={disabled}
            MenuProps={{ disableScrollLock: true }}
            value={value?.time || ''}
            onChange={(e) => update({ time: e.target.value })}
          >
            {REPORT_TIME_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <Typography variant="body2">{option.label}</Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      {error ? (
        <Typography variant="caption" color="error">
          {error}
        </Typography>
      ) : (
        <Typography variant="caption" color="text.secondary">
          Reports for this organization will be generated every{' '}
          {formatWeeklyReportSchedule(value)}{' '}
          {getTimeZoneLabel(value?.timeZone)}.
        </Typography>
      )}
    </Box>
  );
};

OrgScheduleEditor.propTypes = {
  disabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.shape({
    time: PropTypes.string,
    timeZone: PropTypes.string,
    weekday: PropTypes.string,
  }).isRequired,
};

export default OrgScheduleEditor;
